import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IFormSection } from "../../model/form/FormSection";
import FormSectionPresentationModel, {
  IFormSectionPresentationModel
} from "../form/model/FormSectionPresentationModel";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import GetFormReportInteractor from "../../interactor/form/GetFormReportInteractor";
import { IFormVersion } from "../../model/form/FormVersion";
import { IReport1Applicant } from "../../model/report/Report1Applicant";
import { IFormVersionItem } from "../../model/report/FormVersionItem";
import { IFormTypeItem } from "../../model/report/FormTypeItem";
import GetSelectedLanguageInteractor from "../../interactor/settings/GetSelectedLanguageInteractor";

export interface TPortalFormDetailsPresenter extends TLoadingAwarePresenter {
  formSections: IFormSectionPresentationModel[];
  pdfReportVisbile: boolean;
  formVersionId: number;
  hasPdfAccess: boolean;
  pdfReportData: IFormSectionPresentationModel[];
  formVersion: IFormVersionItem;
  applicant: IReport1Applicant;
  selectedVersion: IFormVersion;
  formType: IFormTypeItem;
}

export interface IPortalFormDetailsPresenter
  extends TPortalFormDetailsPresenter,
    TPresentable {
  setForm(
    formVersion: IFormVersion,
    formSections: IFormSection[],
    hasPdfAccess: boolean
  ): void;
  generatePdfReport(): Promise<void>;
}

const defaultState: TPortalFormDetailsPresenter = {
  formSections: [],
  pdfReportVisbile: false,
  formVersionId: 0,
  hasPdfAccess: false,
  pdfReportData: [] as IFormSectionPresentationModel[],
  formVersion: {} as IFormVersionItem,
  applicant: {} as IReport1Applicant,
  selectedVersion: {} as IFormVersion,
  formType: {} as IFormTypeItem
};

const PortalFormDetailsPresenter = withStore<
  IPortalFormDetailsPresenter,
  TPortalFormDetailsPresenter
>(({ application, store, loader, translate }): IPortalFormDetailsPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TPortalFormDetailsPresenter>();

  const setForm = (
    formVersion: IFormVersion,
    formSections: IFormSection[],
    hasPdfAccess: boolean
  ) => {
    loader.start("portalFormDetailsLoader");
    _store.update({
      selectedVersion: formVersion,
      formType: { name: formVersion.reportingTypeName } as IFormTypeItem,
      formSections,
      hasPdfAccess
    });
    loader.stop("portalFormDetailsLoader");
  };

  const showPDFReport = () => {
    _store.update({ pdfReportVisbile: true });
  };

  const pdfReportDataPresentationModelMapper = (
    formSections: IFormSection[]
  ) => {
    return formSections.map((formSection: IFormSection) => {
      return FormSectionPresentationModel(formSection);
    });
  };

  const getPDFReportData = async (formVersionId: number) => {
    try {
      loader.start("pdfReportLoader");

      let currentLanguage = await _application.container
        .resolve<GetSelectedLanguageInteractor>("getSelectedLanguage")
        .execute();
      if (!currentLanguage) {
        currentLanguage = _application.container.resolve<number>("language");
      }

      const { applicant, formVersion, formSections } =
        await _application.container
          .resolve<GetFormReportInteractor>("getFormReport")
          .execute(formVersionId, currentLanguage);

      _store.update({
        applicant,
        formVersion,
        pdfReportData: pdfReportDataPresentationModelMapper(formSections)
      });

      loader.stop("pdfReportLoader");
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const generatePdfReport = async () => {
    const { formVersionId } = _store.getState<IPortalFormDetailsPresenter>();

    if (formVersionId) {
      await getPDFReportData(formVersionId);
      showPDFReport();
    }
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    setForm,
    generatePdfReport
  };
}, defaultState);
export default PortalFormDetailsPresenter;
